import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, ArrowLeft } from 'lucide-react';

export interface CampaignConfig {
  /** Unique identifier — used for localStorage dismiss key and section aria-label */
  id: string;
  /** ISO date string YYYY-MM-DD — the day of the campaign */
  peakDate: string;
  /** Days before peakDate to start showing the countdown */
  daysBeforeStart: number;
  /** Days after peakDate to keep showing as "shop now" */
  daysAfterEnd: number;
  /** Where the CTA links to */
  ctaPath: string;
  /** Headline + supporting copy per language */
  copy: {
    en: { headline: string; sub: string; cta: string };
    ar: { headline: string; sub: string; cta: string };
  };
  /** Background gradient classes (tailwind) */
  gradient?: string;
}

interface CampaignBannerProps {
  language: string;
  config: CampaignConfig;
}

function getNextPeakDate(monthDay: string): Date {
  // monthDay format: YYYY-MM-DD
  // We want the NEXT occurrence — if this year's date has passed, use next year
  const parts = monthDay.split('-').map(Number);
  const [givenYear, month, day] = parts;
  const now = new Date();
  const thisYearTarget = new Date(now.getFullYear(), month - 1, day);
  if (now.getTime() > thisYearTarget.getTime() + 24 * 60 * 60 * 1000) {
    // peak day has already passed by more than 1 day → use next year
    return new Date(now.getFullYear() + 1, month - 1, day);
  }
  // If the year in config is the same as a real future year, prefer it; otherwise use this year's
  return givenYear > now.getFullYear() ? new Date(givenYear, month - 1, day) : thisYearTarget;
}

/**
 * Reusable seasonal-campaign banner.
 *
 * Lifecycle:
 *   - hidden                                      until (peakDate - daysBeforeStart)
 *   - "Coming up — countdown ## days"              between start and peakDate
 *   - "Today!"                                     on peakDate
 *   - "Last day to shop" / "Shop now"             between peakDate and (peakDate + daysAfterEnd)
 *   - hidden                                      after
 *
 * Use this for Mother's Day (March 21 in Egypt), Valentine's, Eid al-Fitr, etc.
 *
 * Implementation note: uses a single setInterval to update the countdown
 * every minute so the rendered day count stays current without us having
 * to remount the component. Skips Date.now during SSR — the banner is
 * client-rendered only, which is fine because Cloudflare bot prerender
 * worker doesn't need to see seasonal banners.
 */
const CampaignBanner = ({ language, config }: CampaignBannerProps) => {
  const isAr = language === 'ar';
  const Arrow = isAr ? ArrowLeft : ArrowRight;
  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(interval);
  }, []);

  if (now === null) return null;

  const peak = getNextPeakDate(config.peakDate);
  const start = new Date(peak.getTime() - config.daysBeforeStart * 24 * 60 * 60 * 1000);
  const end = new Date(peak.getTime() + (config.daysAfterEnd + 1) * 24 * 60 * 60 * 1000);

  if (now < start.getTime() || now >= end.getTime()) return null;

  const msPerDay = 24 * 60 * 60 * 1000;
  const daysUntilPeak = Math.ceil((peak.getTime() - now) / msPerDay);
  const isPeakDay = daysUntilPeak === 0 || (daysUntilPeak === 1 && now >= peak.getTime());
  const isPastPeak = now >= peak.getTime();

  const copy = isAr ? config.copy.ar : config.copy.en;

  const countdownLabel = (() => {
    if (isPeakDay) {
      return isAr ? 'النهارده!' : 'Today!';
    }
    if (isPastPeak) {
      return isAr ? 'آخر فرصة' : 'Last chance';
    }
    if (daysUntilPeak === 1) {
      return isAr ? 'بكره' : 'Tomorrow';
    }
    return isAr
      ? `باقي ${daysUntilPeak} يوم`
      : `${daysUntilPeak} days to go`;
  })();

  return (
    <aside
      role="region"
      aria-label={config.id}
      className={`${config.gradient || 'bg-gradient-to-r from-pink-500 to-rose-500'} text-white`}
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <div className="container mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1">
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/20 rounded-full text-xs font-semibold flex-shrink-0">
            <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
            <span>{countdownLabel}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm sm:text-base leading-tight">{copy.headline}</p>
            <p className="text-xs sm:text-sm text-white/90 leading-tight mt-0.5">{copy.sub}</p>
          </div>
        </div>
        <Link
          to={config.ctaPath}
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-white text-foreground font-semibold rounded-md hover:bg-white/90 transition-colors text-sm flex-shrink-0"
        >
          {copy.cta}
          <Arrow className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </aside>
  );
};

/**
 * Mother's Day in Egypt is March 21 every year (not the US/UK date).
 * Bouqueh uses this as one of its biggest commercial moments.
 */
export const MOTHERS_DAY_EGYPT: CampaignConfig = {
  id: 'mothers-day-egypt',
  peakDate: '2027-03-21',
  daysBeforeStart: 21,
  daysAfterEnd: 0,
  ctaPath: '/en-eg/occasion/mothers-day',
  gradient: 'bg-gradient-to-r from-pink-500 via-rose-500 to-pink-600',
  copy: {
    en: {
      headline: "Mother's Day — March 21",
      sub: 'Same-day delivery across Cairo, Giza, Alexandria.',
      cta: "Shop Mother's Day",
    },
    ar: {
      headline: 'عيد الأم — ٢١ مارس',
      sub: 'بنوصّل في نفس اليوم في القاهرة والجيزة والإسكندرية.',
      cta: 'تسوق عيد الأم',
    },
  },
};

/**
 * Valentine's Day — February 14
 */
export const VALENTINES_DAY: CampaignConfig = {
  id: 'valentines-day',
  peakDate: '2027-02-14',
  daysBeforeStart: 14,
  daysAfterEnd: 0,
  ctaPath: '/en-eg/occasion/valentines-day',
  gradient: 'bg-gradient-to-r from-rose-600 to-red-500',
  copy: {
    en: {
      headline: "Valentine's Day — Feb 14",
      sub: 'Red roses, same-day delivery across Egypt.',
      cta: "Shop Valentine's",
    },
    ar: {
      headline: 'عيد الحب — ١٤ فبراير',
      sub: 'ورد أحمر، توصيل في نفس اليوم في كل مصر.',
      cta: 'تسوق عيد الحب',
    },
  },
};

export default CampaignBanner;
